// CheckPaymentToss.js - 토스페이먼츠 v2 SDK 방식 (샌드박스 코드 적용)
import { ANONYMOUS, loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpGet, httpUrl } from "../api/httpClient";
import Banner from "../components/BannerSlider";
import config from "../config/config";
import backArrow from "../img/common/backarrow.png";
import close from "../img/common/circleClose.png";
import copy from "../img/common/copy.png";
import infoIcon from "../img/home/information.png";
import "../styles/main.scss";

export default function CheckPaymentToss() {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedCoupon, setSelectedCoupon] = useState(
    location.state?.selectedCoupon || null,
  );
  const [ticketInfo, setTicketInfo] = useState(null);
  const [banners, setBanners] = useState([]);

  // 안내사항 API 상태
  const [branchPurchaseConfirmGuideText, setBranchPurchaseConfirmGuideText] =
    useState("");

  // 토스페이먼츠 v2 SDK 관련 상태 (샌드박스 방식)
  const [widgets, setWidgets] = useState(null);
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const lastAmountRef = useRef(50000);

  // ✅ API 중복 호출 방지 (React Strict Mode 대응)
  const hasCalledApiRef = useRef(false);

  // URL 복사 알림 상태
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // SK 먼저 정의 (다른 useMemo에서 사용하므로)
  const SK = useMemo(() => window?.SKYSUNNY || {}, []);

  // 배너 데이터 (API로만 불러옴)
  const bannerImages2 = useMemo(() => {
    return banners || [];
  }, [banners]);

  const movePage = (path) => navigate(path);

  // 토스페이먼츠 설정 (config.js에서 환경별 키 자동 선택)
  const [clientKey, setClientKey] = useState(() => {
    const skKey =
      typeof window !== "undefined" && window.SKYSUNNY?.tossClientKey;
    return (
      skKey ||
      (typeof process !== "undefined" &&
        (process.env?.REACT_APP_TOSS_CLIENT_KEY_TEST ||
          process.env?.REACT_APP_TOSS_CLIENT_KEY)) ||
      config.TOSS_CLIENT_KEY
    );
  });

  useEffect(() => {
    if (clientKey) {
      console.log(
        "[CheckPaymentToss] Using clientKey:",
        clientKey.slice(0, 12) + "...",
      );
      console.log("[CheckPaymentToss] Current Environment:", config.ENV);
    }
  }, [clientKey]);

  // 샌드박스에서는 ANONYMOUS 사용 (공식 샘플 방식)
  const customerKey = useMemo(() => {
    return ANONYMOUS;
  }, []);

  // ===== 공통 유틸 =====
  const parseAmount = useCallback((v) => {
    if (v == null || v === undefined) return 0;

    if (typeof v === "number") {
      return Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
    }

    if (typeof v === "string") {
      const cleaned = v.replace(/[^\d]/g, "");
      const num = Number(cleaned);
      return Number.isFinite(num) && num >= 0 ? Math.floor(num) : 0;
    }

    return 0;
  }, []);

  // passKind: 영문 코드 + 한국어 명칭 모두 정규화
  const passKind = useMemo(() => {
    const raw = String(ticketInfo?.passType || ticketInfo?.type || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    if (raw === "studyroom" || raw === "이용권" || raw.includes("스터디"))
      return "studyroom";
    if (raw === "cash" || raw.includes("캐시")) return "cash"; // "캐시이용권", "캐시정기권" → cash
    if (raw === "free" || raw.includes("자유석")) return "free";
    if (raw === "fix" || raw.includes("고정석")) return "fix";
    if (raw === "1day" || raw.includes("1일")) return "1day";
    if (raw === "locker" || raw.includes("사물함")) return "locker";
    return raw;
  }, [ticketInfo]);

  const legacyPassTypeLabel = useMemo(() => {
    switch (passKind) {
      case "cash":
        return "캐시정기권";
      case "free":
        return "기간정기권 (자유석)";
      case "fix":
        return "기간정기권 (고정석)";
      case "1day":
        return "1일 이용권";
      case "locker":
        return "사물함";
      default:
        return ticketInfo?.passType || "-";
    }
  }, [passKind, ticketInfo?.passType]);

  // 스터디룸 전용 정규화
  const normalizedStudy = useMemo(() => {
    console.log("🔍 [normalizedStudy] useMemo 실행됨");
    console.log("🔍 [normalizedStudy] passKind:", passKind);
    console.log("🔍 [normalizedStudy] ticketInfo:", ticketInfo);

    if (passKind !== "studyroom") {
      console.log("🔍 [normalizedStudy] 스터디룸이 아님 - null 반환");
      return null;
    }
    const t = ticketInfo ?? {};

    const storeName = t.storeName ?? t.store ?? "-";
    const roomName =
      t.roomName ?? t.productName ?? t.selectedTicket?.name ?? "-";

    // ✅ 가격 정보 우선순위: price > priceValue > selectedTicket.price
    const usageAmountValue = parseAmount(
      t.price ?? t.priceValue ?? t.selectedTicket?.price ?? t.totalAmount ?? 0,
    );
    const usageAmountText = usageAmountValue
      ? `${usageAmountValue.toLocaleString()}원`
      : "-";

    // ✅ 백엔드에서 정확히 내려오는 필드명과 변수명 일치
    // usageInfo: "2시간" (이용기간에 표시)
    // usagePeriod: "2026-02-04 오후 01:30 - 오후 02:30" (이용정보에 표시)
    // 우선순위: serverData > ticketInfo 직접 필드
    const usageInfo = t.serverData?.usageInfo ?? t.usageInfo ?? "-";
    const usagePeriod = t.serverData?.usagePeriod ?? t.usagePeriod ?? "-";

    console.log("🏢 [normalizedStudy] 스터디룸 데이터 정규화:", {
      storeName,
      roomName,
      usageAmountValue,
      usageInfo,
      usagePeriod,
      "데이터 출처": {
        "usageInfo 출처": t.serverData?.usageInfo
          ? "serverData"
          : t.usageInfo
            ? "ticketInfo"
            : "none",
        "usagePeriod 출처": t.serverData?.usagePeriod
          ? "serverData"
          : t.usagePeriod
            ? "ticketInfo"
            : "none",
        "t.serverData?.usageInfo": t.serverData?.usageInfo,
        "t.usageInfo": t.usageInfo,
        "t.serverData?.usagePeriod": t.serverData?.usagePeriod,
        "t.usagePeriod": t.usagePeriod,
      },
      "ticketInfo 원본": t,
    });

    const result = {
      storeName,
      passTypeLabel: "스터디룸",
      productName: roomName,
      usageAmountValue,
      usageAmountText,
      usageInfo, // 이용기간: "2시간"
      usagePeriod, // 이용정보: "2025-12-03 오후 04:00 - 오후 06:00"
    };

    console.log("🏢 [normalizedStudy] 반환값:", result);
    return result;
  }, [ticketInfo, passKind, parseAmount]);

  // 할인 & 결제금액
  const legacyPrice = useMemo(() => {
    console.log("🔍 [legacyPrice] useMemo 실행됨");
    console.log("🔍 [legacyPrice] ticketInfo:", ticketInfo);
    const price = ticketInfo?.selectedTicket?.price;
    const parsed = parseAmount(price);
    console.log("💰 [legacyPrice] 계산:", {
      "ticketInfo?.selectedTicket?.price": price,
      parsed: parsed,
      "ticketInfo 전체": ticketInfo,
    });
    return parsed;
  }, [ticketInfo, parseAmount]);

  // 쿠폰 할인 금액 계산 (타입에 따라 다르게 계산)
  const discount = useMemo(() => {
    if (!selectedCoupon) return 0;

    // 서버 필드: discountType ('rate' 또는 'price'), discountValue (숫자), discountAmount (표시용 문자열)
    const couponType = selectedCoupon.discountType || "price";
    const couponValue =
      selectedCoupon.discountValue ?? selectedCoupon.discountAmount ?? 0;

    const originalPrice =
      passKind === "studyroom"
        ? (normalizedStudy?.usageAmountValue ?? 0)
        : legacyPrice;

    if (couponType === "rate") {
      // 할인율 (%)
      const rate =
        typeof couponValue === "number"
          ? couponValue
          : parseAmount(couponValue);
      const discountAmount = Math.floor(originalPrice * (rate / 100));
      console.log("💳 [discount] 할인율 계산:", {
        couponType: "rate",
        rate: rate + "%",
        originalPrice,
        discountAmount,
        쿠폰정보: selectedCoupon,
      });
      return discountAmount;
    } else {
      // 금액제 (price)
      const discountAmount =
        typeof couponValue === "number"
          ? couponValue
          : parseAmount(couponValue);
      console.log("💳 [discount] 금액제 계산:", {
        couponType: "price",
        discountAmount,
        쿠폰정보: selectedCoupon,
      });
      return discountAmount;
    }
  }, [selectedCoupon, passKind, normalizedStudy, legacyPrice, parseAmount]);

  const finalAmount = useMemo(() => {
    let result;
    if (passKind === "studyroom") {
      const base = normalizedStudy?.usageAmountValue ?? 0;
      result = Math.max(base - discount, 0);
    } else {
      result = Math.max(legacyPrice - discount, 0);
    }

    console.log("💰 [finalAmount] 계산:", {
      passKind,
      legacyPrice,
      discount,
      "selectedCoupon?.discountType": selectedCoupon?.discountType,
      "selectedCoupon?.discountAmount": selectedCoupon?.discountAmount,
      "result (before check)": result,
      "ticketInfo?.selectedTicket": ticketInfo?.selectedTicket,
    });

    if (result <= 0) {
      console.warn(
        "[CheckPaymentToss] 계산된 금액이 0 이하입니다. 기본값 1000원 사용",
      );
      result = 1000;
    }

    return result;
  }, [
    passKind,
    normalizedStudy,
    legacyPrice,
    discount,
    selectedCoupon,
    ticketInfo,
  ]);

  const finalAmountText = useMemo(() => {
    if (passKind === "studyroom") {
      const base = normalizedStudy?.usageAmountValue ?? 0;
      return `${Math.max(base - discount, 0).toLocaleString()}원`;
    }
    const total = Math.max(legacyPrice - discount, 0);
    return `${total.toLocaleString()}원`;
  }, [passKind, normalizedStudy, legacyPrice, discount]);

  // 배너 API 호출
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        console.log("[CheckPaymentToss] 배너 API 호출 시작 - type: sub2");
        const response = await httpGet(
          httpUrl.bannerList("sub2", 0, 20),
          [],
          {},
          true,
        );

        console.log("[CheckPaymentToss] 배너 API 응답:", response);

        if (response?.code === 100 && response?.result) {
          const bannerList = response.result;
          console.log("[CheckPaymentToss] 배너 데이터:", bannerList);
          setBanners(bannerList);
        } else {
          console.warn(
            "[CheckPaymentToss] 배너 데이터 없음 또는 오류:",
            response,
          );
        }
      } catch (error) {
        console.error("[CheckPaymentToss] 배너 로드 실패:", error);
      }
    };

    fetchBanners();
  }, []);

  // storeId를 메모이제이션: 실제 값이 바뀔 때만 재계산
  const guideStoreId = useMemo(
    () =>
      SK?.storeId ||
      SK?.storeID ||
      ticketInfo?.selectedTicket?.storeId ||
      ticketInfo?.storeId ||
      SK?.store?.id ||
      (typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("storeId")) ||
      null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [SK, ticketInfo?.selectedTicket?.storeId, ticketInfo?.storeId],
  );

  // 안내사항 가져오기 - guideStoreId가 실제로 바뀔 때만 실행
  useEffect(() => {
    if (!guideStoreId) return;

    const getBranchPurchaseConfirmGuide = async () => {
      try {
        // /user/guide?type=branchPurchaseConfirmGuide&storeId={storeId}
        const result = await httpGet(
          httpUrl.userGuide,
          ["branchPurchaseConfirmGuide", String(guideStoreId)],
          null,
          false,
        );
        console.log(
          "[CheckPaymentToss] branchPurchaseConfirmGuide API 응답:",
          result,
        );

        if (result?.code === 100 && result?.result?.value) {
          setBranchPurchaseConfirmGuideText(result.result.value);
        }
      } catch (err) {
        console.error(
          "[CheckPaymentToss] branchPurchaseConfirmGuide API 실패:",
          err,
        );
      }
    };

    getBranchPurchaseConfirmGuide();
  }, [guideStoreId]);

  // ✅ RN에서 postMessage로 전송한 usageInfo, usagePeriod 수신
  useEffect(() => {
    const handleRNMessage = (event) => {
      try {
        // ReactNativeWebView에서 전송한 메시지 처리
        const message =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        console.log("📨 [CheckPaymentToss] RN 메시지 수신:", message);

        if (message.action === "UPDATE_USAGE_INFO" && message.data) {
          console.log(
            "✅ [CheckPaymentToss] usageInfo 업데이트:",
            message.data,
          );

          setTicketInfo((prev) => {
            const updated = {
              ...prev,
              usageInfo: message.data.usageInfo || prev?.usageInfo,
              usagePeriod: message.data.usagePeriod || prev?.usagePeriod,
              serverData: {
                ...(prev?.serverData || {}),
                usageInfo:
                  message.data.usageInfo || prev?.serverData?.usageInfo,
                usagePeriod:
                  message.data.usagePeriod || prev?.serverData?.usagePeriod,
              },
            };

            console.log(
              "✅ [CheckPaymentToss] ticketInfo 업데이트 완료:",
              updated,
            );
            return updated;
          });
        }
      } catch (error) {
        console.error("❌ [CheckPaymentToss] RN 메시지 파싱 실패:", error);
      }
    };

    // ReactNativeWebView 메시지 리스너
    if (window.ReactNativeWebView) {
      document.addEventListener("message", handleRNMessage);
      window.addEventListener("message", handleRNMessage);
    }

    return () => {
      document.removeEventListener("message", handleRNMessage);
      window.removeEventListener("message", handleRNMessage);
    };
  }, []);

  // RN 데이터 주입 확인 및 ticketInfo 업데이트
  useEffect(() => {
    const checkRNData = () => {
      if (window.SKYSUNNY && Object.keys(window.SKYSUNNY).length > 0) {
        console.log("[CheckPaymentToss] RN 데이터 감지됨:", window.SKYSUNNY);
      }

      // ✅ window.ticketInfo도 확인 (RN에서 직접 주입한 경우)
      if (window.ticketInfo && Object.keys(window.ticketInfo).length > 0) {
        console.log(
          "[CheckPaymentToss] window.ticketInfo 감지됨:",
          window.ticketInfo,
        );

        // ticketInfo 상태 업데이트 (usageInfo, usagePeriod 포함)
        setTicketInfo((prev) => {
          if (!prev) return window.ticketInfo;

          // 기존 데이터와 병합 (window.ticketInfo 우선)
          const merged = {
            ...prev,
            usageInfo: window.ticketInfo.usageInfo || prev.usageInfo,
            usagePeriod: window.ticketInfo.usagePeriod || prev.usagePeriod,
            serverData: {
              ...(prev.serverData || {}),
              usageInfo:
                window.ticketInfo.usageInfo || prev.serverData?.usageInfo,
              usagePeriod:
                window.ticketInfo.usagePeriod || prev.serverData?.usagePeriod,
            },
          };

          console.log("[CheckPaymentToss] ticketInfo 업데이트:", merged);
          return merged;
        });

        // ✅ tossClientKey도 업데이트 체크 (RN 주입 대응)
        if (
          window.SKYSUNNY?.tossClientKey &&
          window.SKYSUNNY.tossClientKey !== clientKey
        ) {
          console.log(
            "[CheckPaymentToss] tossClientKey 업데이트 감지됨:",
            window.SKYSUNNY.tossClientKey.slice(0, 12) + "...",
          );
          setClientKey(window.SKYSUNNY.tossClientKey);
        }
      }
    };

    const interval = setInterval(checkRNData, 500); // 500ms마다 체크 (더 빠르게)
    setTimeout(() => clearInterval(interval), 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // ✅ cleanup 함수에서 사용할 isMounted 플래그
    let isMounted = true;

    console.log("🌟 [useEffect:onInit] 마운트됨");
    console.log("🌟 [useEffect:onInit] window.SKYSUNNY:", window.SKYSUNNY);
    console.log("🌟 [useEffect:onInit] window.ticketInfo:", window.ticketInfo);
    console.log(
      "🌟 [useEffect:onInit] window.__askRN 존재:",
      typeof window.__askRN === "function",
    );

    // ✅ 중복 호출 방지: 이미 API를 호출했으면 리턴
    if (hasCalledApiRef.current) {
      console.log("⚠️ [useEffect:onInit] 이미 API 호출됨 - 중복 방지");
      return;
    }

    // URL 파라미터에서 먼저 orderNumber 확인 (가장 빠름)
    const params = new URLSearchParams(window.location.search);
    const orderFromUrl = params.get("orderNumber");

    // ✅ 대리인 결제: parentToken 처리 (API 호출 전에 먼저 저장!)
    const parentToken = params.get("parentToken");
    if (parentToken) {
      console.log(
        "🔑 [useEffect:onInit] parentToken 발견 - localStorage에 저장",
      );
      localStorage.setItem("accessToken", parentToken);
      sessionStorage.setItem("parentToken", parentToken);
    }

    // ✅ orderNumber가 있으면 즉시 플래그 설정 (최우선)
    if (orderFromUrl) {
      hasCalledApiRef.current = true;
      console.log(
        "🔒 [useEffect:onInit] API 호출 플래그 설정 완료 (orderNumber:",
        orderFromUrl,
        ")",
      );
    }

    // 결제 실패 시 처리 (앱에서 알림 처리하므로 URL만 정리)
    const failStatus = params.get("fail");
    if (failStatus === "1") {
      console.log("❌ [useEffect:onInit] 결제 실패로 돌아옴");
      // URL에서 fail 파라미터 제거
      const newUrl =
        window.location.pathname +
        (orderFromUrl ? `?orderNumber=${orderFromUrl}` : "");
      window.history.replaceState({}, "", newUrl);
    }

    const onInit = (e) => {
      console.log("🌟 [onInit] 이벤트 수신:", e.detail);

      const defaultTicketInfo = {
        storeName: "매장",
        passType: "cash",
        selectedTicket: {
          name: "상품",
          price: "1000원",
          id: 1,
        },
      };

      // ✅ window.ticketInfo에서도 데이터 가져오기 (RN에서 주입한 값)
      const ticketInfoFromWindow = window.ticketInfo || {};
      console.log("🌟 [onInit] window.ticketInfo:", ticketInfoFromWindow);

      // ✅ 스터디룸 판단: passType이 studyroom/study_room/이용권이거나, purchaseInfo/productInfo에 "스터디룸" 포함
      const purchaseInfo =
        e.detail?.purchaseInfo ||
        e.detail?.productName ||
        e.detail?.productInfo ||
        ticketInfoFromWindow?.purchaseInfo ||
        "";
      const rawPassType = String(
        e.detail?.passType || ticketInfoFromWindow?.passType || "",
      ).toLowerCase();
      const isStudyRoomByPassType =
        rawPassType === "studyroom" ||
        rawPassType === "study_room" ||
        rawPassType === "이용권";
      const isStudyRoomByName = purchaseInfo.includes("스터디룸");
      const isStudyRoom = isStudyRoomByPassType || isStudyRoomByName;
      const detectedPassType = isStudyRoom
        ? "studyroom"
        : e.detail?.passType ||
          ticketInfoFromWindow?.passType ||
          defaultTicketInfo.passType;

      console.log("🌟 [onInit] 스터디룸 판단:", {
        purchaseInfo,
        isStudyRoom,
        detectedPassType,
        "e.detail?.passType": e.detail?.passType,
      });

      const mergedInfo = {
        ...defaultTicketInfo,
        ...e.detail,
        passType: detectedPassType,
        selectedTicket: {
          ...defaultTicketInfo.selectedTicket,
          ...e.detail?.selectedTicket,
        },
        // ✅ RN에서 전달받은 추가 필드 명시적으로 병합 (우선순위: window.ticketInfo > e.detail > selectedTicket)
        validDays:
          ticketInfoFromWindow?.validDays ||
          e.detail?.validDays ||
          defaultTicketInfo.validDays,
        dailyUsableHours:
          ticketInfoFromWindow?.dailyUsableHours ||
          e.detail?.dailyUsableHours ||
          e.detail?.selectedTicket?.dailyUsableHours ||
          "",
        oneDayInfo: e.detail?.oneDayInfo || e.detail?.usageInfo || "",
        productName:
          e.detail?.productName || e.detail?.selectedTicket?.name || "",
        // ✅ 스터디룸 전용 필드 추가
        usagePeriod:
          ticketInfoFromWindow?.usagePeriod || e.detail?.usagePeriod || "",
        usageInfo: ticketInfoFromWindow?.usageInfo || e.detail?.usageInfo || "",
        priceValue:
          ticketInfoFromWindow?.priceValue ||
          e.detail?.priceValue ||
          e.detail?.price ||
          0,
        roomName:
          ticketInfoFromWindow?.roomName ||
          e.detail?.roomName ||
          e.detail?.purchaseInfo ||
          "",
      };

      // orderNumber 확인 (우선순위: URL 파라미터 > e.detail)
      // URL 파라미터가 가장 빠르고 확실함
      if (orderFromUrl) {
        console.log(
          "🌟 [onInit] ✅ orderNumber 발견 (URL - 최우선):",
          orderFromUrl,
        );
        mergedInfo.orderNumber = orderFromUrl;
        localStorage.setItem("lastOrderNumber", orderFromUrl);
      } else if (e.detail?.orderNumber) {
        console.log(
          "🌟 [onInit] ✅ orderNumber 발견 (e.detail):",
          e.detail.orderNumber,
        );
        mergedInfo.orderNumber = e.detail.orderNumber;
        localStorage.setItem("lastOrderNumber", e.detail.orderNumber);
      } else {
        // localStorage에서 마지막 orderNumber 확인
        const lastOrderNumber = localStorage.getItem("lastOrderNumber");
        if (lastOrderNumber) {
          console.log(
            "🌟 [onInit] ✅ orderNumber 발견 (localStorage):",
            lastOrderNumber,
          );
          mergedInfo.orderNumber = lastOrderNumber;
        } else {
          console.log("🌟 [onInit] ⚠️ orderNumber 없음");
        }
      }

      console.log("🌟 [onInit] ticketInfo 업데이트:", mergedInfo);
      setTicketInfo((prev) => {
        if (!prev) return mergedInfo;
        // fetchOrderInfo가 이미 설정한 데이터가 있으면 selectedTicket.name 등 중요 필드를 보존
        return {
          ...mergedInfo,
          selectedTicket: {
            ...mergedInfo.selectedTicket,
            // purchaseInfo가 있는 쪽(더 완전한 이름) 우선
            name:
              (prev.selectedTicket?.name?.length || 0) >
              (mergedInfo.selectedTicket?.name?.length || 0)
                ? prev.selectedTicket.name
                : mergedInfo.selectedTicket?.name || prev.selectedTicket?.name,
          },
          // onReply로 이미 업데이트된 필드 보존
          validDays: prev.validDays || mergedInfo.validDays,
          usageInfo: prev.usageInfo || mergedInfo.usageInfo,
          usagePeriod: prev.usagePeriod || mergedInfo.usagePeriod,
          oneDayInfo: prev.oneDayInfo || mergedInfo.oneDayInfo,
        };
      });
    };

    document.addEventListener("skysunny:init", onInit);

    // URL 파라미터가 있으면 서버에서 주문 정보 가져오기 (대리인 결제)
    if (orderFromUrl) {
      console.log(
        "🌟 [useEffect:onInit] ⚡ URL에서 orderNumber 발견 - 서버에서 주문 정보 가져오기:",
        orderFromUrl,
      );

      // 서버에서 주문 정보 가져오기
      const fetchOrderInfo = async () => {
        try {
          // ✅ isMounted 체크 - cleanup 후에는 실행하지 않음
          if (!isMounted) {
            console.log(
              "⚠️ [fetchOrderInfo] 컴포넌트가 언마운트됨 - API 호출 취소",
            );
            return;
          }

          // ✅ 대리인 결제인지 확인 (parentToken 존재 여부)
          const isParentPayment = !!parentToken;
          const apiUrl = isParentPayment
            ? `${httpUrl.draftOrder}?orderNumber=${orderFromUrl}&parentToken=${parentToken}` // 대리인 결제용 API (parentToken 포함)
            : httpUrl.getOrderInfo(orderFromUrl); // 일반 사용자용 API

          console.log("🌟 [fetchOrderInfo] 주문 정보 API 호출 시작:", {
            orderNumber: orderFromUrl,
            isParentPayment,
            apiUrl,
            hasAccessToken: !!localStorage.getItem("accessToken"),
            parentToken: parentToken ? "***존재***" : "null",
          });
          const response = await httpGet(apiUrl, [], {}, true);

          // ✅ API 응답 후 다시 isMounted 체크
          if (!isMounted) {
            console.log(
              "⚠️ [fetchOrderInfo] API 응답 중 컴포넌트가 언마운트됨 - 상태 업데이트 취소",
            );
            return;
          }

          console.log("🌟 [fetchOrderInfo] 주문 정보 API 응답:", response);

          if (response?.code === 100 && response?.result) {
            const orderData = response.result;
            console.log("🌟 [fetchOrderInfo] 주문 데이터:", orderData);
            console.log("🌟 [fetchOrderInfo] usageInfo 확인:", {
              "orderData.usageInfo": orderData.usageInfo,
              "orderData.usagePeriod": orderData.usagePeriod,
              "orderData 전체": orderData,
            });

            // ✅ 스터디룸 판단: passType이 studyroom/study_room/이용권이거나, purchaseInfo/productInfo에 "스터디룸" 포함
            const rawOrderPassType = String(
              orderData.passType || "",
            ).toLowerCase();
            const isStudyRoomByPassType =
              rawOrderPassType === "studyroom" ||
              rawOrderPassType === "study_room" ||
              rawOrderPassType === "이용권";
            const isStudyRoomByName = (
              orderData.purchaseInfo ||
              orderData.productName ||
              orderData.productInfo ||
              ""
            ).includes("스터디룸");
            const isStudyRoom = isStudyRoomByPassType || isStudyRoomByName;
            const actualPassType = isStudyRoom
              ? "studyroom"
              : orderData.passType || "cash";

            console.log("🏢 [fetchOrderInfo] 스터디룸 판단:", {
              purchaseInfo: orderData.purchaseInfo,
              productName: orderData.productName,
              isStudyRoom,
              actualPassType,
            });

            // ✅ RN에서 전달한 데이터 확인 (window.ticketInfo)
            const rnTicketInfo = window.ticketInfo || {};
            console.log(
              "🔵 [fetchOrderInfo] RN에서 전달한 데이터:",
              rnTicketInfo,
            );

            // window.SKYSUNNY.selectedTicket을 fallback으로 사용 (API가 subDescription 등을 안 내려줄 경우 대비)
            const rnSelectedTicket = window.SKYSUNNY?.selectedTicket || {};

            // 서버 응답을 ticketInfo 형식으로 변환
            const ticketInfoFromServer = {
              orderNumber: orderFromUrl,
              storeName:
                orderData.storeName || rnSelectedTicket.storeName || "매장",
              passType: actualPassType,
              selectedTicket: {
                name:
                  orderData.purchaseInfo ||
                  orderData.productName ||
                  rnSelectedTicket.name ||
                  "상품",
                price:
                  orderData.price ||
                  orderData.amount ||
                  rnSelectedTicket.price ||
                  0,
                priceText: orderData.price
                  ? `${orderData.price.toLocaleString()}원`
                  : orderData.amount
                    ? `${orderData.amount.toLocaleString()}원`
                    : rnSelectedTicket.priceText || "0원",
                id: orderData.passId || rnSelectedTicket.id || 1,
                reward:
                  orderData.validDays || rnSelectedTicket.reward || "30일",
                subDescription:
                  orderData.subDescription ||
                  rnSelectedTicket.subDescription ||
                  "",
                dailyUsableHours:
                  orderData.dailyUsableHours ||
                  rnSelectedTicket.dailyUsableHours ||
                  "",
              },
              validDays: orderData.validDays || "",
              oneDayInfo: orderData.usageInfo || "",
              dailyUsableHours: orderData.dailyUsableHours || "",
              productName:
                orderData.productName || orderData.purchaseInfo || "",
              // ✅ 스터디룸 전용 필드 추가 (RN 데이터 우선, 없으면 API 응답 사용)
              price: orderData.price || orderData.amount || 0,
              priceValue: orderData.price || orderData.amount || 0,
              usagePeriod:
                rnTicketInfo.usagePeriod || orderData.usagePeriod || "",
              usageInfo: rnTicketInfo.usageInfo || orderData.usageInfo || "",
              roomName: orderData.purchaseInfo || orderData.productName || "",
              // 서버에서 받은 원본 데이터도 저장
              serverData: {
                ...orderData,
                // ✅ RN 데이터를 serverData에도 저장 (최우선)
                usagePeriod: rnTicketInfo.usagePeriod || orderData.usagePeriod,
                usageInfo: rnTicketInfo.usageInfo || orderData.usageInfo,
              },
            };

            console.log(
              "🌟 [fetchOrderInfo] ticketInfo 변환 완료:",
              ticketInfoFromServer,
            );
            console.log("🌟 [fetchOrderInfo] 변환된 데이터 확인:", {
              "ticketInfoFromServer.usageInfo": ticketInfoFromServer.usageInfo,
              "ticketInfoFromServer.usagePeriod":
                ticketInfoFromServer.usagePeriod,
              "ticketInfoFromServer.serverData":
                ticketInfoFromServer.serverData,
            });
            setTicketInfo(ticketInfoFromServer);
            localStorage.setItem("lastOrderNumber", orderFromUrl);
          } else {
            console.warn(
              "🌟 [fetchOrderInfo] 주문 정보를 찾을 수 없음:",
              response,
            );
            // 기본 데이터로 초기화
            onInit({ detail: { orderNumber: orderFromUrl } });
          }
        } catch (error) {
          console.error("🌟 [fetchOrderInfo] 주문 정보 로드 실패:", error);
          // 에러 발생 시 기본 데이터로 초기화
          onInit({ detail: { orderNumber: orderFromUrl } });
        }
      };

      fetchOrderInfo();
    } else if (window.SKYSUNNY && Object.keys(window.SKYSUNNY).length > 0) {
      console.log("🌟 [useEffect:onInit] window.SKYSUNNY 존재 - 즉시 호출");
      onInit({ detail: window.SKYSUNNY });
    } else {
      console.log(
        "🌟 [useEffect:onInit] orderNumber 대기 중... (RN 데이터 또는 URL 필요)",
      );
      // 기본 데이터로 초기화 (orderNumber는 나중에 업데이트됨)
      onInit({ detail: {} });
    }

    return () => {
      // ✅ cleanup: isMounted를 false로 설정하여 진행 중인 비동기 작업 취소
      isMounted = false;
      document.removeEventListener("skysunny:init", onInit);
      console.log("🧹 [useEffect:onInit] cleanup 실행 - isMounted = false");
    };
  }, []);

  // RN reply 수신 → serverAmount, dailyUsableHours, validDays, usageInfo, usagePeriod 즉시 반영
  useEffect(() => {
    const onReply = async (e) => {
      const payload = e?.detail || {};
      console.log("🔄 [skysunny:reply] 이벤트 수신:", payload);

      if (payload?.action !== "REQUEST_DRAFT") return;

      const d = payload?.data || {};
      const serverAmount =
        (typeof d.serverAmount === "number" ? d.serverAmount : null) ??
        (typeof d?.order?.amount === "number" ? d.order.amount : null) ??
        (typeof d.orderAmount === "number" ? d.orderAmount : null);

      // ✅ Draft 상세 정보 추출 (최상위 레벨과 data 레벨 모두 확인)
      const validDays = payload?.validDays || d?.validDays || null;
      const dailyUsableHours =
        payload?.dailyUsableHours || d?.dailyUsableHours || null;
      const usageInfo = payload?.usageInfo || d?.usageInfo || null;
      const usagePeriod = payload?.usagePeriod || d?.usagePeriod || null;

      console.log("🔄 [skysunny:reply] Draft 정보 수신:", {
        serverAmount,
        validDays,
        dailyUsableHours,
        usageInfo,
        usagePeriod,
        "payload 전체": payload,
      });

      // ✅ ticketInfo 업데이트 (serverAmount, validDays, dailyUsableHours, usageInfo, usagePeriod)
      setTicketInfo((prev) => {
        const updated = {
          ...(prev || {}),
          ...(Number.isInteger(serverAmount) && serverAmount > 0
            ? { serverAmount }
            : {}),
          ...(validDays ? { validDays } : {}),
          ...(dailyUsableHours ? { dailyUsableHours } : {}),
          ...(usageInfo ? { usageInfo } : {}),
          ...(usagePeriod ? { usagePeriod } : {}),
          // ✅ serverData에도 저장
          serverData: {
            ...(prev?.serverData || {}),
            ...(usageInfo ? { usageInfo } : {}),
            ...(usagePeriod ? { usagePeriod } : {}),
          },
        };

        console.log("🔄 [skysunny:reply] ticketInfo 업데이트:", updated);
        return updated;
      });

      // ✅ 위젯 금액 동기화
      if (Number.isInteger(serverAmount) && serverAmount > 0) {
        try {
          if (widgets) {
            await widgets.setAmount({ currency: "KRW", value: serverAmount });
            lastAmountRef.current = serverAmount;
          }
        } catch (err) {
          console.warn(
            "[CheckPaymentToss] serverAmount 위젯 동기화 실패:",
            err,
          );
        }
      }
    };
    document.addEventListener("skysunny:reply", onReply);
    return () => document.removeEventListener("skysunny:reply", onReply);
  }, [widgets]);

  // 쿠폰 상태 동기화
  useEffect(() => {
    console.log("🔍 [쿠폰 상태 동기화] useEffect 실행됨");
    console.log("🔍 [쿠폰 상태 동기화] location.state 전체:", location.state);
    console.log("🔍 [쿠폰 상태 동기화] 현재 ticketInfo 상태:", ticketInfo);
    console.log(
      "🔍 [쿠폰 상태 동기화] 현재 selectedCoupon 상태:",
      selectedCoupon,
    );

    // ✅ 쿠폰 페이지에서 돌아올 때 ticketInfo도 함께 복원 (기존 값과 병합)
    if (location.state?.ticketInfo) {
      console.log("🔄 [ticketInfo 복원] location.state에서 복원 시작");
      console.log("🔄 [ticketInfo 복원] 복원 전 ticketInfo:", ticketInfo);
      console.log(
        "🔄 [ticketInfo 복원] location.state.ticketInfo:",
        location.state.ticketInfo,
      );

      // ✅ 기존 ticketInfo와 병합 (기존 값 우선 - usageInfo, usagePeriod 등 유지)
      setTicketInfo((prev) => {
        const merged = {
          ...location.state.ticketInfo, // 새로운 값 (기본)
          ...prev, // 기존 값 (우선순위 높음)
          // 특정 필드는 명시적으로 유지 (덮어씌워지지 않도록)
          usageInfo: prev?.usageInfo || location.state.ticketInfo?.usageInfo,
          usagePeriod:
            prev?.usagePeriod || location.state.ticketInfo?.usagePeriod,
          dailyUsableHours:
            prev?.dailyUsableHours ||
            location.state.ticketInfo?.dailyUsableHours,
          validDays: prev?.validDays || location.state.ticketInfo?.validDays,
        };

        console.log("🔄 [ticketInfo 복원] 병합 결과:", merged);
        console.log("🔄 [ticketInfo 복원] 비교:", {
          "복원 전 usageInfo": prev?.usageInfo,
          "복원할 usageInfo": location.state.ticketInfo?.usageInfo,
          "병합 후 usageInfo": merged.usageInfo,
          "복원 전 usagePeriod": prev?.usagePeriod,
          "복원할 usagePeriod": location.state.ticketInfo?.usagePeriod,
          "병합 후 usagePeriod": merged.usagePeriod,
        });

        return merged;
      });
      console.log("🔄 [ticketInfo 복원] 복원 완료 - setTicketInfo 호출됨");
    } else {
      console.log("⚠️ [ticketInfo 복원] location.state.ticketInfo가 없음");
    }

    if ("selectedCoupon" in (location.state || {})) {
      const coupon = location.state.selectedCoupon || null;
      console.log("🎫 [쿠폰 선택됨]:", {
        userCouponId: coupon?.userCouponId || coupon?.id,
        name: coupon?.name || coupon?.title,
        discountType: coupon?.discountType || coupon?.type,
        discountAmount: coupon?.discountAmount || coupon?.amount,
        설명:
          (coupon?.discountType || coupon?.type) === "rate"
            ? "할인율(%)"
            : "금액제(원)",
        "전체 쿠폰 정보": coupon,
      });
      setSelectedCoupon(coupon);
    } else {
      console.log("⚠️ [쿠폰 선택] location.state에 selectedCoupon 없음");
    }
  }, [location.state]);

  // ✅ ticketInfo를 sessionStorage에 저장 (페이지 새로고침 대응)
  useEffect(() => {
    if (ticketInfo) {
      sessionStorage.setItem(
        "checkPayment:ticketInfo",
        JSON.stringify(ticketInfo),
      );
      console.log("💾 [ticketInfo 저장] sessionStorage에 저장 완료");
    }
  }, [ticketInfo]);

  // ✅ 컴포넌트 마운트 시 sessionStorage에서 ticketInfo 복원
  useEffect(() => {
    const savedTicketInfo = sessionStorage.getItem("checkPayment:ticketInfo");
    if (savedTicketInfo && !ticketInfo) {
      try {
        const parsed = JSON.parse(savedTicketInfo);
        setTicketInfo(parsed);
        console.log("🔄 [ticketInfo 복원] sessionStorage에서 복원:", parsed);
      } catch (error) {
        console.error("❌ [ticketInfo 복원 실패]:", error);
      }
    }
  }, []);

  // 토스페이먼츠 위젯 초기화 (샌드박스 방식 적용)
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 50; // 최대 5초 대기 (100ms * 50)

    async function initializePaymentWidget() {
      try {
        console.log("[CheckPaymentToss] 위젯 초기화 시작");

        if (!clientKey) {
          throw new Error("clientKey가 없습니다");
        }

        // DOM 요소 확인 (재시도 로직 개선)
        const paymentMethodElement = document.getElementById("payment-method");
        const agreementElement = document.getElementById("agreement");

        if (!paymentMethodElement || !agreementElement) {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(initializePaymentWidget, 100);
            return;
          } else {
            throw new Error("결제 위젯 DOM 요소를 찾을 수 없습니다");
          }
        }

        if (!isMounted) return;

        // 기존 내용 초기화
        paymentMethodElement.innerHTML = "";
        agreementElement.innerHTML = "";

        console.log("[CheckPaymentToss] 토스 SDK 로드 시작...");
        const startTime = performance.now();

        // 토스페이먼츠 SDK 로드
        const tossPayments = await loadTossPayments(clientKey);
        if (!isMounted) return;

        console.log(
          `[CheckPaymentToss] 토스 SDK 로드 완료 (${Math.round(
            performance.now() - startTime,
          )}ms)`,
        );

        // 위젯 인스턴스 생성 (샌드박스에서는 ANONYMOUS 사용)
        const widgets = tossPayments.widgets({
          customerKey: customerKey,
        });
        if (!isMounted) return;

        // 금액 설정 (샌드박스 방식: setAmount 먼저 호출)
        const amount = {
          currency: "KRW",
          value: finalAmount && finalAmount > 0 ? finalAmount : 50000, // 기본 50,000원
        };

        await widgets.setAmount(amount);

        console.log("[CheckPaymentToss] 위젯 렌더링 시작...");
        const renderStartTime = performance.now();

        // 결제 수단과 약관 렌더링 (기본 설정 사용)
        await Promise.all([
          widgets.renderPaymentMethods({
            selector: "#payment-method",
            variantKey: "sky-sunny",
          }),
          widgets.renderAgreement({
            selector: "#agreement",
            variantKey: "AGREEMENT",
          }),
        ]);

        console.log(
          `[CheckPaymentToss] 위젯 렌더링 완료 (${Math.round(
            performance.now() - renderStartTime,
          )}ms)`,
        );

        // iframe에 referrerPolicy 설정
        setTimeout(() => {
          const iframes = document.querySelectorAll(
            "#payment-method iframe, #agreement iframe",
          );
          iframes.forEach((iframe) => {
            iframe.setAttribute("referrerpolicy", "no-referrer");
            iframe.setAttribute(
              "sandbox",
              "allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation",
            );
          });
        }, 100);

        if (!isMounted) return;

        setWidgets(widgets);
        setIsPaymentReady(true);
        lastAmountRef.current = amount.value;

        console.log(
          `[CheckPaymentToss] ✅ 전체 초기화 완료 (${Math.round(
            performance.now() - startTime,
          )}ms)`,
        );
      } catch (error) {
        console.error("[CheckPaymentToss] 결제 위젯 초기화 실패:", error);
        if (isMounted) {
          setIsPaymentReady(false);
          const paymentMethodElement =
            document.getElementById("payment-method");
          if (paymentMethodElement) {
            paymentMethodElement.innerHTML = `
              <div style="padding: 20px; text-align: center; color: #ff6b6b; border: 1px solid #ff6b6b; border-radius: 8px; background: #fff5f5;">
                <div style="font-weight: bold; margin-bottom: 8px;">⚠️ 결제 위젯 로드 실패</div>
                <div style="font-size: 12px; line-height: 1.4;">
                  ${error.message}<br/>
                  <small style="color: #999;">clientKey: ${clientKey}</small>
                </div>
              </div>
            `;
          }
        }
      }
    }

    // 초기화 시작
    initializePaymentWidget();

    return () => {
      isMounted = false;
      try {
        const paymentMethodElement = document.getElementById("payment-method");
        const agreementElement = document.getElementById("agreement");
        if (paymentMethodElement) paymentMethodElement.innerHTML = "";
        if (agreementElement) agreementElement.innerHTML = "";
      } catch (error) {
        console.warn("[CheckPaymentToss] 위젯 정리 중 오류:", error);
      }
    };
  }, [clientKey, customerKey]);

  // 금액 변경 시 위젯 업데이트
  useEffect(() => {
    if (!widgets || !isPaymentReady) return;

    const newAmount = finalAmount && finalAmount > 0 ? finalAmount : 50000;

    if (lastAmountRef.current === newAmount) return;

    const updateAmount = async () => {
      try {
        await widgets.setAmount({
          currency: "KRW",
          value: newAmount,
        });
        lastAmountRef.current = newAmount;
      } catch (error) {
        console.error("[CheckPaymentToss] 금액 업데이트 오류:", error);
      }
    };

    updateAmount();
  }, [finalAmount, widgets, isPaymentReady]);

  // 랜덤 문자열 생성 함수 (샌드박스 방식)
  const generateRandomString = () => window.btoa(Math.random()).slice(0, 20);

  // 실제 주문번호 가져오기
  const getActualOrderNumber = useCallback(() => {
    console.log("[getActualOrderNumber] 시작 - 전체 상태:", {
      "SK?.order?.id": SK?.order?.id,
      "SK?.orderNumber": SK?.orderNumber,
      "ticketInfo?.orderNumber": ticketInfo?.orderNumber,
      "window.SKYSUNNY": window.SKYSUNNY,
    });

    // 1순위: window.SKYSUNNY에서 order.id 또는 orderNumber
    if (SK?.order?.id) {
      console.log("[getActualOrderNumber] ✅ SK.order.id 발견:", SK.order.id);
      return SK.order.id;
    }
    if (SK?.orderNumber) {
      console.log(
        "[getActualOrderNumber] ✅ SK.orderNumber 발견:",
        SK.orderNumber,
      );
      return SK.orderNumber;
    }

    // 2순위: sessionStorage의 toss:draft
    try {
      const draftStr = sessionStorage.getItem("toss:draft");
      console.log(
        "[getActualOrderNumber] sessionStorage toss:draft 확인:",
        draftStr,
      );
      if (draftStr) {
        const draft = JSON.parse(draftStr);
        if (draft?.orderNumber) {
          console.log(
            "[getActualOrderNumber] ✅ sessionStorage draft.orderNumber 발견:",
            draft.orderNumber,
          );
          return draft.orderNumber;
        }
      }
    } catch (error) {
      console.warn(
        "[getActualOrderNumber] sessionStorage draft parse error:",
        error,
      );
    }

    // 3순위: ticketInfo에서 orderNumber (RN에서 전달한 경우)
    if (ticketInfo?.orderNumber) {
      console.log(
        "[getActualOrderNumber] ✅ ticketInfo.orderNumber 발견:",
        ticketInfo.orderNumber,
      );
      return ticketInfo.orderNumber;
    }

    // 4순위: localStorage의 lastOrderNumber
    const lastOrderNumber = localStorage.getItem("lastOrderNumber");
    if (lastOrderNumber) {
      console.log(
        "[getActualOrderNumber] ✅ localStorage lastOrderNumber 발견:",
        lastOrderNumber,
      );
      return lastOrderNumber;
    }

    // 5순위: URL 파라미터
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const orderFromUrl = params.get("orderNumber") || params.get("orderId");
      if (orderFromUrl) {
        console.log(
          "[getActualOrderNumber] ✅ URL orderNumber 발견:",
          orderFromUrl,
        );
        // URL에서 받은 orderNumber를 localStorage에 저장
        localStorage.setItem("lastOrderNumber", orderFromUrl);
        return orderFromUrl;
      }
    }

    console.error(
      "[getActualOrderNumber] ❌ 주문번호를 어디서도 찾을 수 없습니다!",
    );
    return null;
  }, [SK, ticketInfo]);

  // 토스페이먼츠 샌드박스 방식: 구매하기 버튼 클릭
  const onClickBuy = async () => {
    console.log("🔵 [onClickBuy] 구매하기 버튼 클릭됨");
    console.log("🔵 [onClickBuy] widgets:", !!widgets);
    console.log("🔵 [onClickBuy] isPaymentReady:", isPaymentReady);
    console.log(
      "🔵 [onClickBuy] window.__askRN 존재:",
      typeof window.__askRN === "function",
    );
    console.log("🔵 [onClickBuy] window.SKYSUNNY:", window.SKYSUNNY);

    if (!widgets || !isPaymentReady) {
      alert("결제 위젯 준비 중입니다. 잠시 후 다시 시도하세요.");
      return;
    }

    try {
      // 실제 주문번호 가져오기 (orderNumber: 사용자용 주문번호)
      console.log("🔵 [onClickBuy] getActualOrderNumber 호출 시작");
      let orderNumber = getActualOrderNumber();
      console.log("🔵 [onClickBuy] getActualOrderNumber 결과:", orderNumber);

      // 주문번호가 없으면 RN에 임시 주문 생성 요청
      if (!orderNumber) {
        console.log(
          "🟡 [onClickBuy] 주문번호가 없음 - RN에 임시 주문 생성 요청 시작",
        );

        // RN에 임시 주문 생성 요청 (window.__askRN 사용)
        if (typeof window.__askRN === "function") {
          try {
            console.log("🟡 [onClickBuy] window.__askRN 함수 존재 확인됨");

            // Promise로 응답 대기
            const draftPromise = new Promise((resolve, reject) => {
              console.log("🟡 [onClickBuy] Promise 생성 - 응답 대기 시작");

              const timeout = setTimeout(() => {
                console.error("🔴 [onClickBuy] 10초 타임아웃 - RN 응답 없음");
                reject(new Error("임시 주문 생성 타임아웃 (10초)"));
              }, 10000); // 10초 타임아웃

              // 응답 리스너 등록
              const handleReply = (event) => {
                const detail = event.detail || {};
                console.log("🟢 [onClickBuy] skysunny:reply 이벤트 수신:", {
                  action: detail.action,
                  type: detail.type,
                  ok: detail.ok,
                  orderNumber: detail.orderNumber,
                  error: detail.error,
                  fullDetail: detail,
                });

                if (
                  detail.action === "REQUEST_DRAFT" ||
                  detail.type === "REQUEST_DRAFT"
                ) {
                  console.log("🟢 [onClickBuy] REQUEST_DRAFT 응답 확인됨");
                  clearTimeout(timeout);
                  document.removeEventListener("skysunny:reply", handleReply);

                  if (detail.ok && detail.orderNumber) {
                    console.log(
                      "🟢 [onClickBuy] 성공 - orderNumber:",
                      detail.orderNumber,
                    );
                    resolve(detail);
                  } else {
                    const errorMsg = detail.error || "임시 주문 생성 실패";
                    console.error("🔴 [onClickBuy] 실패 - error:", errorMsg);
                    console.error("🔴 [onClickBuy] 전체 응답:", detail);
                    reject(new Error(errorMsg));
                  }
                }
              };

              console.log("🟡 [onClickBuy] skysunny:reply 이벤트 리스너 등록");
              document.addEventListener("skysunny:reply", handleReply);
            });

            // RN에 요청 전송
            const requestPayload = {
              storeName: ticketInfo?.storeName,
              productName:
                ticketInfo?.selectedTicket?.name || ticketInfo?.productName,
              amount: finalAmount || 50000,
              passType: passKind || "cash",
            };
            console.log(
              "🟡 [onClickBuy] RN에 REQUEST_DRAFT 전송:",
              requestPayload,
            );
            window.__askRN("REQUEST_DRAFT", requestPayload);
            console.log(
              "🟡 [onClickBuy] REQUEST_DRAFT 전송 완료 - 응답 대기 중...",
            );

            // 응답 대기
            const draftResult = await draftPromise;
            console.log(
              "🟢 [onClickBuy] Promise 완료 - draftResult:",
              draftResult,
            );

            if (draftResult?.orderNumber) {
              orderNumber = draftResult.orderNumber;
              const dbOrderId =
                draftResult.data?.order?.id || draftResult.data?.orderId;

              console.log("🟢 [onClickBuy] RN 응답 데이터:", {
                orderNumber,
                dbOrderId,
                설명: {
                  orderNumber: "토스에 전달할 주문번호",
                  dbOrderId: "DB orders 테이블 PK",
                },
              });

              // sessionStorage에 DB orderId 저장 (CompletePayment에서 사용)
              if (dbOrderId) {
                sessionStorage.setItem("dbOrderId", String(dbOrderId));
                console.log(
                  "🟢 [onClickBuy] sessionStorage에 dbOrderId 저장:",
                  dbOrderId,
                );
              }

              // window.SKYSUNNY에 저장
              if (window.SKYSUNNY) {
                window.SKYSUNNY.orderNumber = orderNumber;
                window.SKYSUNNY.orderId = dbOrderId;
                if (draftResult.data?.order) {
                  window.SKYSUNNY.order = draftResult.data.order;
                }
                if (draftResult.tossClientKey) {
                  window.SKYSUNNY.tossClientKey = draftResult.tossClientKey;
                }
                console.log(
                  "🟢 [onClickBuy] window.SKYSUNNY 업데이트 완료:",
                  window.SKYSUNNY,
                );
              }
            }
          } catch (error) {
            // console.error('🔴 [onClickBuy] RN 임시 주문 생성 실패:', error);
            // console.error('🔴 [onClickBuy] 에러 상세:', {
            //     message: error.message,
            //     stack: error.stack,
            //     error: error
            // });

            // 사용자에게 더 명확한 에러 메시지 표시
            // let userMessage = error.message || '알 수 없는 오류';
            // if (error.message?.includes('타임아웃')) {
            //     userMessage = '결제 준비에 시간이 오래 걸리고 있습니다.\n\n가능한 원인:\n• 네트워크 연결 불안정\n• 서버 응답 지연\n\n잠시 후 다시 시도해주세요.';
            // }

            // alert(`임시 주문 생성에 실패했습니다.\n\n${userMessage}`);
            return;
          }
        } else {
          // console.error('🔴 [onClickBuy] window.__askRN 함수가 없습니다!');
          // console.error('🔴 [onClickBuy] window 객체:', Object.keys(window).filter(k => k.includes('ask') || k.includes('RN') || k.includes('SKYSUNNY')));
        }

        // 여전히 주문번호가 없으면 에러 처리
        if (!orderNumber) {
          return;
        }
      }

      console.log("[onClickBuy] 결제 진행 - orderNumber:", orderNumber);

      // ⭐ 2단계: UPDATE_PAYMENT 호출 (쿠폰 적용 및 최종 금액 업데이트)
      console.log("💳 [onClickBuy] UPDATE_PAYMENT 호출 시작");
      console.log(
        "💳 [onClickBuy] window.__askRN 타입:",
        typeof window.__askRN,
      );
      console.log("💳 [onClickBuy] window.__askRN 함수:", window.__askRN);

      const originalAmount =
        passKind === "studyroom"
          ? normalizedStudy?.usageAmountValue || 0
          : legacyPrice;

      // ⭐ 쿠폰 ID를 숫자로 확실하게 변환
      let couponId = null;
      if (selectedCoupon?.id) {
        const parsedId =
          typeof selectedCoupon.id === "number"
            ? selectedCoupon.id
            : Number(selectedCoupon.id);

        // NaN 체크
        if (Number.isFinite(parsedId) && parsedId > 0) {
          couponId = parsedId;
        } else {
          console.error(
            "🔴 [onClickBuy] 유효하지 않은 쿠폰 ID:",
            selectedCoupon.id,
          );
        }
      }

      const updatePaymentPayload = {
        orderNumber: orderNumber,
        amount: finalAmount,
        originalAmount: originalAmount,
      };

      // ✅ 쿠폰 사용 시에만 userCouponId 추가
      const userCouponId = selectedCoupon?.userCouponId || selectedCoupon?.id;
      if (userCouponId) {
        updatePaymentPayload.userCouponId = userCouponId;
      }

      console.log(
        "💳 [onClickBuy] UPDATE_PAYMENT 페이로드:",
        updatePaymentPayload,
      );
      console.log(
        "💳 [onClickBuy] UPDATE_PAYMENT 페이로드 (JSON):",
        JSON.stringify(updatePaymentPayload, null, 2),
      );
      console.log("💳 [onClickBuy] 상세 정보:", {
        주문번호: orderNumber,
        최종금액: finalAmount,
        원래금액: originalAmount,
        할인금액: discount,
        "쿠폰ID (userCouponId)": userCouponId || "null",
        쿠폰타입: selectedCoupon?.discountType || "null",
        쿠폰할인값: selectedCoupon?.discountAmount || "null",
        쿠폰사용: selectedCoupon ? "사용" : "미사용",
        "updatePaymentPayload에 originalAmount 포함?":
          "originalAmount" in updatePaymentPayload,
        "updatePaymentPayload에 userCouponId 포함?":
          "userCouponId" in updatePaymentPayload,
        "updatePaymentPayload.originalAmount 값":
          updatePaymentPayload.originalAmount,
      });

      // RN에 UPDATE_PAYMENT 요청 (Promise 기반으로 개선)
      if (typeof window.__askRN === "function") {
        try {
          console.log(
            "💳 [onClickBuy] ========== UPDATE_PAYMENT 시작 ==========",
          );
          console.log(
            "💳 [onClickBuy] window.__askRN 호출 직전 - payload:",
            JSON.stringify(updatePaymentPayload),
          );

          // ✅ window.__askRN이 Promise를 반환하므로 직접 await 사용
          const updateResponse = await window.__askRN(
            "UPDATE_PAYMENT",
            updatePaymentPayload,
          );

          console.log("✅ [onClickBuy] UPDATE_PAYMENT 성공!");
          console.log("✅ [onClickBuy] 응답 데이터:", updateResponse);
          console.log(
            "💳 [onClickBuy] ========== UPDATE_PAYMENT 완료 ==========",
          );
        } catch (error) {
          console.error(
            "🔴 [onClickBuy] ========== UPDATE_PAYMENT 실패 ==========",
          );
          console.error("🔴 [onClickBuy] 에러 타입:", error?.constructor?.name);
          console.error("🔴 [onClickBuy] 에러 메시지:", error?.message);
          console.error("🔴 [onClickBuy] 에러 전체:", error);
          console.error("🔴 [onClickBuy] 에러 스택:", error?.stack);
          alert(`결제 정보 업데이트에 실패했습니다.\n\n${error.message}`);
          return;
        }
      } else {
        console.warn(
          "⚠️ [onClickBuy] window.__askRN 없음 - UPDATE_PAYMENT 스킵",
        );
        console.warn(
          "⚠️ [onClickBuy] window 객체 키:",
          Object.keys(window).filter(
            (k) => k.includes("ask") || k.includes("RN"),
          ),
        );
      }

      const orderName = ticketInfo?.selectedTicket?.name || "스카이써니 이용권";
      const customerName = SK?.customerName || ticketInfo?.customerName || "";
      const customerEmail =
        SK?.customerEmail || ticketInfo?.customerEmail || "";

      // sessionStorage에 주문 정보 저장
      const draftData = {
        orderNumber: orderNumber,
        storeName: ticketInfo?.storeName || "",
        passKind: passKind || "",
        passType: passKind || "",
        productName: orderName,
        finalAmount: finalAmount,
        validDays:
          ticketInfo?.selectedTicket?.reward || ticketInfo?.validDays || "",
        usageInfo: ticketInfo?.oneDayInfo || ticketInfo?.usageInfo || "",
        // ✅ dailyUsableHours 추가 (기간정기권 자유석용)
        dailyUsableHours:
          ticketInfo?.dailyUsableHours ||
          ticketInfo?.selectedTicket?.dailyUsableHours ||
          null,
        // ✅ usagePeriod 추가 (스터디룸용)
        usagePeriod: ticketInfo?.usagePeriod || null,
        timestamp: Date.now(),
      };

      console.log(
        "[CheckPaymentToss] 💾 sessionStorage 저장 데이터:",
        draftData,
      );
      sessionStorage.setItem("toss:draft", JSON.stringify(draftData));

      // 성공/실패 URL 설정
      const baseUrl = window.location.origin;

      // ✅ 성공: payment-processing 페이지로 이동
      const successParams = new URLSearchParams({
        orderNumber: orderNumber,
        amount: finalAmount.toString(),
        storeName: ticketInfo?.storeName || "매장",
        passType: passKind || "cash",
        productName: orderName,
        status: "success",
      });
      const successUrl = `${baseUrl}/payment-processing?${successParams.toString()}`;

      // ✅ 실패: fail-payment 페이지로 이동
      const failParams = new URLSearchParams({
        orderNumber: orderNumber,
        status: "fail",
        message: "결제가 취소되었습니다",
      });
      const failUrl = `${baseUrl}/fail-payment?${failParams.toString()}`;

      // 토스페이먼츠에 전달할 랜덤 주문 ID 생성 (임시 주석처리)
      // const tossOrderId = generateRandomString();

      console.log("[onClickBuy] 🔍 토스 결제 데이터:", {
        // tossOrderId,
        orderNumber,
        dbOrderId: sessionStorage.getItem("dbOrderId"),
        설명: {
          // 'tossOrderId': '토스에 전달하는 랜덤 ID (예: MC4yOTUxMDk0ODgzNzcy)',
          orderNumber: "사용자용 주문번호 (예: 20251001000033)",
          dbOrderId: "DB orders 테이블 PK (예: 166)",
        },
      });

      // sessionStorage에 매핑 정보 저장 (CompletePayment에서 사용)
      sessionStorage.setItem(
        "tossOrderIdMapping",
        JSON.stringify({
          // tossOrderId: tossOrderId,
          orderNumber: orderNumber,
        }),
      );

      // 토스페이먼츠 결제 요청
      await widgets?.requestPayment({
        orderId: orderNumber, // 우리 orderNumber 사용
        orderName: orderName,
        customerName: customerName,
        customerEmail: customerEmail,
        successUrl: successUrl,
        failUrl: failUrl,
      });
    } catch (error) {
      // 사용자 취소는 조용히 처리
      if (error?.code === "USER_CANCEL") {
        return;
      }

      // 앱 연동 실패 시 조용히 처리
      if (
        error?.message?.includes("intent") ||
        error?.message?.includes("scheme")
      ) {
        return;
      }

      const errorMessage =
        error?.message || "결제 요청 중 오류가 발생했습니다.";
      alert(`결제 요청 중 오류가 발생했습니다.\n${errorMessage}`);
    }
  };

  return (
    <div className="container checkout-page">
      {/* 상단 바 */}
      <div className="top-bar">
        <div
          className="top-bar-left"
          onClick={() => {
            try {
              // RN WebView 환경
              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                  JSON.stringify({ type: "GO_BACK" }),
                );
              }
              // RN 커스텀 브리지
              else if (window.__askRN) {
                window.__askRN("GO_BACK", {});
              }
              // 웹 환경
              else {
                navigate(-1);
              }
            } catch (error) {
              navigate(-1);
            }
          }}
          style={{
            cursor: "pointer",
            padding: "8px",
            minWidth: "40px",
            minHeight: "40px",
            zIndex: 1000,
          }}
        >
          <img
            src={backArrow}
            alt="뒤로가기"
            className="icon24"
            style={{ pointerEvents: "none" }}
          />
        </div>
        <div className="top-bar-center">
          <span className="top-txt font-noto">구매확인</span>
        </div>
        {/* <div className="top-bar-right">
                    <button
                        onClick={() => setShowDebugInfo(!showDebugInfo)}
                        style={{
                            border: '1px solid #ccc',
                            background: '#fff',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            borderRadius: '4px'
                        }}
                    >
                        🔍
                    </button>
                </div> */}
      </div>

      {/* 본문 */}
      <div className="content-scroll">
        {/* 디버깅 정보 (개발용) */}
        {/* {showDebugInfo && (
                    <div style={{
                        margin: '10px',
                        padding: '10px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '5px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        maxHeight: '300px',
                        overflow: 'auto'
                    }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>🔍 CheckPaymentToss 디버깅 정보:</div>
                        <div>SK.storeId: {SK?.storeId || 'null'}</div>
                        <div>SK.storeID: {SK?.storeID || 'null'}</div>
                        <div>SK.passId: {SK?.passId || 'null'}</div>
                        <div>SK.selectedTicket: {JSON.stringify(SK?.selectedTicket) || 'null'}</div>
                        <div>SK.accessToken: {SK?.accessToken ? '***있음***' : 'null'}</div>
                        <div>ticketInfo.storeId: {ticketInfo?.storeId || 'null'}</div>
                        <div>ticketInfo.selectedTicket.id: {ticketInfo?.selectedTicket?.id || 'null'}</div>
                        <div>ticketInfo.selectedTicket.storeId: {ticketInfo?.selectedTicket?.storeId || 'null'}</div>
                        <div>localStorage.accessToken: {(typeof localStorage !== 'undefined' && localStorage.getItem('accessToken')) ? '***있음***' : 'null'}</div>
                        <div style={{ marginTop: '10px', fontWeight: 'bold', color: 'blue' }}>전체 window.SKYSUNNY:</div>
                        <div style={{ fontSize: '10px', wordBreak: 'break-all' }}>{JSON.stringify(window.SKYSUNNY, null, 2)}</div>
                        <div style={{ marginTop: '5px', fontWeight: 'bold', color: 'green' }}>전체 ticketInfo:</div>
                        <div style={{ fontSize: '10px', wordBreak: 'break-all' }}>{JSON.stringify(ticketInfo, null, 2)}</div>
                        <div style={{ marginTop: '5px', fontWeight: 'bold', color: 'red' }}>URL 파라미터:</div>
                        <div style={{ fontSize: '10px' }}>{window.location.search}</div>
                    </div>
                )} */}

        {/* 배너 - BannerSlider 컴포넌트 (배너가 없으면 자동으로 null 반환) */}
        <Banner banners={bannerImages2} type="sub2" />

        {/* 구매 정보 */}
        <div className="info-container">
          <div className="section-title-box">
            <span className="font-bm section-title">구매정보</span>
          </div>

          <div className="info-box">
            {(() => {
              console.log("🔍 [렌더링] 구매정보 렌더링 시작");
              console.log("🔍 [렌더링] passKind:", passKind);
              console.log("🔍 [렌더링] normalizedStudy:", normalizedStudy);
              console.log("🔍 [렌더링] ticketInfo:", ticketInfo);
              console.log(
                "🔍 [렌더링] legacyPassTypeLabel:",
                legacyPassTypeLabel,
              );
              return null;
            })()}
            {passKind === "studyroom" ? (
              <>
                {(() => {
                  console.log("🎨 [렌더링] 스터디룸 화면 표시:", {
                    normalizedStudy: normalizedStudy,
                    "normalizedStudy?.usageInfo": normalizedStudy?.usageInfo,
                    "normalizedStudy?.usagePeriod":
                      normalizedStudy?.usagePeriod,
                    ticketInfo: ticketInfo,
                    "ticketInfo?.usageInfo": ticketInfo?.usageInfo,
                    "ticketInfo?.usagePeriod": ticketInfo?.usagePeriod,
                    "ticketInfo?.serverData": ticketInfo?.serverData,
                  });
                  return null;
                })()}
                <div className="info-row">
                  <span className="info-title">매장명</span>
                  <span className="info-text">
                    {normalizedStudy?.storeName}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-title">이용권</span>
                  <span className="info-text">
                    {normalizedStudy?.passTypeLabel}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-title">상품정보</span>
                  <span className="info-text">
                    {normalizedStudy?.productName}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-title">이용금액</span>
                  <span className="info-text">
                    {normalizedStudy?.usageAmountText}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-title">이용기간</span>
                  <span className="info-text">
                    {normalizedStudy?.usageInfo || "데이터 없음"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-title">이용정보</span>
                  <span className="info-text">
                    {normalizedStudy?.usagePeriod || "데이터 없음"}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="info-row">
                  <span className="info-title">매장명</span>
                  <span className="info-text">
                    {ticketInfo?.storeName || "-"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-title">이용권</span>
                  <span className="info-text">{legacyPassTypeLabel}</span>
                </div>
                <div className="info-row">
                  <span className="info-title">상품정보</span>
                  <span className="info-text">
                    {ticketInfo?.selectedTicket?.name || "-"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-title">이용금액</span>
                  <span className="info-text">
                    {ticketInfo?.selectedTicket?.priceText ||
                      ticketInfo?.selectedTicket?.price ||
                      "-"}
                  </span>
                </div>

                {/* 이용기간: 전 타입 공통 */}
                <div className="info-row">
                  <span className="info-title">이용기간</span>
                  <span className="info-text">
                    {ticketInfo?.validDays ||
                      ticketInfo?.selectedTicket?.reward ||
                      "-"}
                  </span>
                </div>

                {/* 캐시정기권: 좌석당 할인율 (이용기간 다음) */}
                {passKind === "cash" && (
                  <div className="info-row">
                    <span className="info-title">좌석당 할인율</span>
                    <span className="info-text">
                      {ticketInfo?.selectedTicket?.subDescription || "-"}
                    </span>
                  </div>
                )}

                {/* 1일 이용권: 1일 이용정보 */}
                {passKind === "1day" && (
                  <div className="info-row">
                    <span className="info-title">1일 이용정보</span>
                    <span className="info-text">
                      {ticketInfo?.oneDayInfo || ticketInfo?.usageInfo || "-"}
                    </span>
                  </div>
                )}
              </>
            )}

            <hr className="line" />

            <div className="info-row">
              <span className="info-title">할인쿠폰</span>
              <button
                className="coupon-btn"
                onClick={() => {
                  try {
                    // 더 광범위한 fallback 로직
                    const storeId =
                      SK?.storeId ||
                      SK?.storeID ||
                      ticketInfo?.selectedTicket?.storeId ||
                      ticketInfo?.storeId ||
                      SK?.store?.id ||
                      // URL 파라미터에서 확인
                      (typeof window !== "undefined" &&
                        new URLSearchParams(window.location.search).get(
                          "storeId",
                        )) ||
                      // 임시 기본값 (테스트용)
                      "5";

                    const passId =
                      ticketInfo?.selectedTicket?.id ||
                      SK?.passId ||
                      SK?.selectedTicket?.id ||
                      // URL 파라미터에서 확인
                      (typeof window !== "undefined" &&
                        new URLSearchParams(window.location.search).get(
                          "passId",
                        )) ||
                      // 임시 기본값 (테스트용)
                      ticketInfo?.selectedTicket?.id ||
                      "1";

                    const accessToken =
                      SK?.accessToken ||
                      (typeof localStorage !== "undefined" &&
                        localStorage.getItem("accessToken")) ||
                      undefined;

                    // price 계산: finalAmount 사용 (할인 적용 전 가격)
                    const price =
                      passKind === "studyroom"
                        ? normalizedStudy?.usageAmountValue || 0
                        : legacyPrice;

                    // ✅ 대리인 결제: parentToken 확인 (URL 파라미터 또는 sessionStorage에서)
                    const params = new URLSearchParams(window.location.search);
                    const parentToken =
                      params.get("parentToken") ||
                      (typeof sessionStorage !== "undefined" &&
                        sessionStorage.getItem("parentToken"));

                    // ✅ parentToken이 있으면 URL 파라미터로 포함
                    const couponUrl = parentToken
                      ? `/check-coupon?parentToken=${encodeURIComponent(
                          parentToken,
                        )}`
                      : "/check-coupon";

                    console.log("[CheckPaymentToss] 쿠폰 페이지 이동:", {
                      couponUrl,
                      hasParentToken: !!parentToken,
                      storeId,
                      passId,
                      price,
                    });

                    navigate(couponUrl, {
                      state: {
                        storeId,
                        passId,
                        price,
                        accessToken,
                        ticketInfo: ticketInfo, // ✅ ticketInfo도 함께 전달
                      },
                    });
                  } catch (err) {
                    console.warn("[CheckPaymentToss] 쿠폰선택 이동 오류", err);
                    navigate("/check-coupon");
                  }
                }}
              >
                쿠폰선택
              </button>
            </div>

            {!selectedCoupon && (
              <div className="info-row coupon-guide-text">
                <span className="info-text coupon-guide-text1">
                  사용하실 쿠폰을 선택해주세요.
                </span>
              </div>
            )}

            <div className="info-row">
              {selectedCoupon && (
                <>
                  <div className="info-text">
                    {selectedCoupon.name || selectedCoupon.title} (
                    {selectedCoupon.discountValue ??
                      selectedCoupon.discountAmount}
                    {selectedCoupon.discountType === "rate" ? "%" : "원"})
                  </div>
                  <div
                    className="info-img"
                    onClick={() => setSelectedCoupon(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={close} alt="쿠폰삭제" className="icon24" />
                  </div>
                </>
              )}
            </div>

            <hr className="dashed-line" />

            <div className="info-row">
              <span className="info-title">할인금액</span>
              <span className="info-text">
                {discount ? `${discount.toLocaleString()}원` : "0원"}
              </span>
            </div>
          </div>
        </div>

        {/* PC/대리인 결제 안내 */}
        <div className="section2-title-box3">
          <p className="note-text font-bm">PC, 대리인 결제도 가능해요!</p>
          <div
            className="copy-url-box"
            onClick={async () => {
              try {
                // 실제 주문번호 가져오기
                const orderNumber = getActualOrderNumber();

                // orderNumber가 없으면 에러 처리
                if (!orderNumber) {
                  console.error("[CheckPaymentToss] orderNumber가 없습니다");
                  alert(
                    "주문번호를 불러올 수 없습니다.\n잠시 후 다시 시도해주세요.",
                  );
                  return;
                }

                // 환경별 URL은 config에서 가져오기
                const baseUrl = config.BASE_URL;

                // 대리인 결제 URL 생성
                const proxyPaymentUrl = `${baseUrl}/parent-login/${orderNumber}`;

                await navigator.clipboard.writeText(proxyPaymentUrl);
                setShowCopyNotification(true);
                setTimeout(() => setShowCopyNotification(false), 3000);

                console.log(
                  "[CheckPaymentToss] 대리인 결제 URL 복사됨:",
                  proxyPaymentUrl,
                );
                console.log("[CheckPaymentToss] orderNumber:", orderNumber);
                console.log("[CheckPaymentToss] 현재 환경:", config.ENV);
              } catch (err) {
                console.error("URL 복사 실패:", err);
                alert("URL 복사에 실패했습니다.");
              }
            }}
            style={{ cursor: "pointer" }}
          >
            {(() => {
              const orderNumber = getActualOrderNumber();
              console.log(
                "[CheckPaymentToss] URL 표시 - orderNumber:",
                orderNumber,
              );

              if (orderNumber) {
                // 화면에는 짧은 URL 표시
                const displayUrl = `http://skasca.me/cash/${orderNumber}`;

                return (
                  <>
                    <span className="font-noto url-text">{displayUrl}</span>
                    <img src={copy} alt="info" className="icon14" />
                    <span className="copy-btn">URL 복사</span>
                  </>
                );
              } else {
                return (
                  <span
                    className="font-noto url-text"
                    style={{ color: "#999" }}
                  >
                    주문번호를 불러오는 중...
                  </span>
                );
              }
            })()}
          </div>
          <div className="line"></div>
        </div>

        {/* 안내사항 */}
        <div className="section2-title-box">
          <img src={infoIcon} alt="info" className="icon14" />
          <div className="text-box">
            <span className="font-bm section-title">안내사항</span>
          </div>
        </div>
        {branchPurchaseConfirmGuideText && (
          <div className="section2-title-box2">
            <p className="note-text font-noto">
              {branchPurchaseConfirmGuideText}
            </p>
          </div>
        )}

        <div className="toss-payment-widget">
          {!isPaymentReady && (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#666",
                fontSize: "14px",
              }}
            >
              결제 위젯을 불러오는 중입니다...
            </div>
          )}

          {/* 결제 방법 위젯 */}
          <div
            id="payment-method"
            style={{ marginBottom: "16px", minHeight: "200px", width: "100%" }}
          ></div>

          {/* 약관 동의 위젯 */}
          <div
            id="agreement"
            style={{ marginBottom: "16px", minHeight: "50px", width: "100%" }}
          ></div>

          <div
            className="toss-payment-widget-footer"
            style={{ marginBottom: "20px", paddingLeft: "20px" }}
          >
            <span
              className="font-noto"
              style={{
                color: "#999999",
                fontSize: "11px",
                lineHeight: "16px",
                whiteSpace: "pre-line",
              }}
            >
              주식회사 스카스카 | 대표 : 김형래 {"\n"}
              주소 : 경기도 남양주시 별내중앙로 30, 305-1849호(별내동){"\n"}
              사업자등록번호 : 137-87-03668 | 통신판매업신고번호 : 미정{"\n"}
              SKASKA All rights reserved
            </span>
          </div>
        </div>

        <div className="scroll-spacer" aria-hidden />
      </div>

      <div
        className={`checkout-footer-fixed ${
          passKind === "studyroom" ? "studyroom-spacing" : ""
        }`}
      >
        <div className="bottom-bar2">
          <span>결제금액</span>
          <span>{finalAmountText}</span>
        </div>
        <div className="bottom-button">
          <button
            onClick={onClickBuy}
            disabled={!isPaymentReady}
            style={{ opacity: isPaymentReady ? 1 : 0.6 }}
          >
            구매하기
          </button>
        </div>
      </div>

      {/* URL 복사 알림 메시지 */}
      {showCopyNotification && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            fontSize: "14px",
            zIndex: 9999,
            fontFamily: "NotoSansKR, sans-serif",
          }}
        >
          URL이 복사되었습니다
        </div>
      )}
    </div>
  );
}
